import { SetNonUndefined, zod } from '@acme/core'
import { z } from 'zod'
import ApiService from '../api.service'
import { AvaliacaoSchema } from './avaliacao'

const endpoint = 'supervisores'

const locais = zod.LocalSchema
const empresas = zod.EmpresaSchema
const centroCustos = zod.CentroCustoSchema

const LocaisPickIdSchema = z
    .array(locais.pick({ id: true }))
    .nullish()
    .transform(value => value?.map(v => v.id))

const EmpresasPickIdSchema = z
    .array(empresas.pick({ id: true }))
    .nullish()
    .transform(value => value?.map(v => v.id))

const CentroCustosPickIdSchema = z
    .array(centroCustos.pick({ id: true }))
    .nullish()
    .transform(value => value?.map(v => v.id))

const CreateSchema = z.object({
    cpf: zod.FuncionarioSchema.shape.cpf,
    nome: z.string().min(1, 'Obrigatório'),
    Usuario: zod.UsuarioSchema.partial().extend({
        email: z.string().trim().email('Email inválido'),
        password: AvaliacaoSchema.shape.senha,
        categoriaId: z.number(),
        perfilPermissaoId: z
            .number()
            .or(z.string())
            .nullish()
            .transform(v => (v == null ? null : Number(v))),
    }),
    localIds: LocaisPickIdSchema,
    empresaIds: EmpresasPickIdSchema,
    centroCustoIds: CentroCustosPickIdSchema,
})

export const CreateSupervisorSchema = CreateSchema.refine(
    data => {
        const {
            localIds,
            empresaIds,
            centroCustoIds,
            Usuario: { categoriaId },
        } = data
        return (
            (categoriaId != 3 &&
                (localIds?.length ||
                    centroCustoIds?.length ||
                    empresaIds?.length)) ||
            categoriaId == 3
        )
    },
    {
        message: 'Deve conter pelo menos um local, empresa ou centro de custo',
        path: ['localIds', 'empresaIds', 'centroCustoIds'],
    },
)

export type CreateSupervisorOutput = z.infer<typeof CreateSchema>

export const UpdateSupervisorSchema = zod.SupervisorSchema.pick({
    id: true,
    nome: true,
    cpf: true,
})
    .partial()
    .required({
        id: true,
    })
    .extend({
        Usuario: zod.UsuarioSchema.pick({
            email: true,
            password: true,
            categoriaId: true,
        })
            .partial()
            .extend({
                perfilPermissaoId: z.coerce.number().nullish(),
            }),
        localIds: LocaisPickIdSchema,
        empresaIds: EmpresasPickIdSchema,
        centroCustoIds: CentroCustosPickIdSchema,
    })

export type UpdateSupervisorOutput = z.infer<typeof UpdateSupervisorSchema>

const SupervisorModel = zod.SupervisorSchema.extend({
    Usuario: zod.UsuarioSchema,
    Locais: z.array(zod.LocalSchema),
    Empresas: z.array(zod.EmpresaSchema),
    CentroCustos: z.array(zod.CentroCustoSchema),
})

export type Supervisor = Omit<z.infer<typeof SupervisorModel>, 'Usuario'> & {
    SupervisorEmpresas?: SupervisorEmpresas[]
    SupervisorCentroCusto?: SupervisorCentroCustos[]
    SupervisorLocal?: SupervisorLocais[]
} & {
    Usuario: SetNonUndefined<
        z.infer<typeof SupervisorModel>['Usuario'],
        'perfilPermissaoId'
    >
}

const service = new ApiService<CreateSupervisorOutput, Supervisor>(endpoint)

// api service locais

const CreateSupervisorLocaisSchema = zod.SupervisorLocaisSchema.pick({
    supervisorId: true,
}).extend({
    localIds: z.array(zod.LocalSchema.pick({ id: true })),
})

type CreateSupervisorLocaisOutput = z.infer<typeof CreateSupervisorLocaisSchema>

export type SupervisorLocais = z.infer<typeof zod.SupervisorLocaisSchema>

const localService = new ApiService<
    CreateSupervisorLocaisOutput,
    SupervisorLocais
>(`${endpoint}/locais`)

// api service supervisor centro custos

const CreateSupervisorCentroCustosSchema =
    zod.SupervisorCentroCustosSchema.pick({
        supervisorId: true,
    }).extend({
        centroCustoIds: z.array(zod.CentroCustoSchema.pick({ id: true })),
    })

type CreateSupervisorCentroCustosOutput = z.infer<
    typeof CreateSupervisorCentroCustosSchema
>

export type SupervisorCentroCustos = z.infer<
    typeof zod.SupervisorCentroCustosSchema
>

const centroCustoService = new ApiService<
    CreateSupervisorCentroCustosOutput,
    SupervisorCentroCustos
>(`${endpoint}/centro-custos`)

// api service supervisor/empresas

const CreateSupervisorEmpresasSchema = zod.SupervisorEmpresasSchema.pick({
    supervisorId: true,
}).extend({
    empresaIds: z.array(zod.EmpresaSchema.pick({ id: true })),
})

type CreateSupervisorEmpresasOutput = z.infer<
    typeof CreateSupervisorEmpresasSchema
>

export type SupervisorEmpresas = z.infer<typeof zod.SupervisorEmpresasSchema>

const empresaService = new ApiService<
    CreateSupervisorEmpresasOutput,
    SupervisorEmpresas
>(`${endpoint}/empresas`)

export const supervisor = Object.assign(service, {
    local: localService,
    centroCusto: centroCustoService,
    empresa: empresaService,
})
